import React from 'react';
import { Icon } from './components.jsx';
// L&T IT Helpdesk - Evolution Matters
// Read-only lookup over the static Evolution client/matter export (~44.5k rows).
// The whole dataset is fetched ONCE (then cached in IndexedDB) and all search /
// filter / sort / paging happens in the browser, so it's instant after load.

const { useState, useEffect, useRef, useMemo } = React;

// Shared API caller - see window.IT_AUTH.api in index.html / src/auth.js.
const emApi = (method, path) => window.IT_AUTH.api(method, path, undefined, 'emApi');

const PAGE_SIZE = 50;

function useIsPhone() {
  const q = '(max-width: 600px)';
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(q).matches);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(q);
    const on = () => setPhone(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return phone;
}

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-GB');
};

// Client/matter always shown zero-padded as 00000.000 (5-digit client, 3-digit
// matter) to match how Evolution / NetDocuments reference them.
const fmtCM = (client, matter) => {
  const c = String(client ?? '').padStart(5, '0');
  return (matter === null || matter === undefined || matter === '')
    ? c
    : `${c}.${String(matter).padStart(3, '0')}`;
};

function StatusTag({ status }) {
  const closed = String(status || '').toLowerCase() === 'closed';
  const bg = closed ? 'var(--st-closed-bg)' : 'var(--st-new-bg)';
  const fg = closed ? 'var(--st-closed)' : 'var(--st-new)';
  return <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 999, background: bg, color: fg }}>{status || '-'}</span>;
}

// Column config. `key` doubles as the sort key. `filter` = header dropdown type:
// 'select' (multi-select checklist, fed by `facet`) or 'date' (from/to range).
// `text` = raw string for CSV/titles.
const COLUMNS = [
  { key: 'client',      label: 'Client / Matter',   w: 130, text: r => fmtCM(r.clientNumber, r.matterRef), cell: r => <span className="em-cm">{fmtCM(r.clientNumber, r.matterRef)}</span> },
  { key: 'name',        label: 'Client name',        w: 220, text: r => r.clientName || '',        cell: r => r.clientName || '-' },
  { key: 'description', label: 'Matter description', w: 380, text: r => r.matterDescription || '', cell: r => r.matterDescription || '-' },
  { key: 'status',      label: 'Status',             w: 110, filter: 'select', facet: 'statuses',    text: r => r.mattStatus || '',    cell: r => <StatusTag status={r.mattStatus}/> },
  { key: 'partner',     label: 'Partner',            w: 100, filter: 'select', facet: 'partners',    text: r => r.matterPartner || '', cell: r => r.matterPartner || '-' },
  { key: 'fe',          label: 'FE',                 w: 90,  filter: 'select', facet: 'fes',         text: r => r.matterFE || '',      cell: r => r.matterFE || '-' },
  { key: 'opened',      label: 'Opened',             w: 110, filter: 'date',   text: r => fmtDate(r.dateOpened),       cell: r => <span className="em-cm">{fmtDate(r.dateOpened)}</span> },
  { key: 'closed',      label: 'Closed',             w: 110, filter: 'date',   text: r => fmtDate(r.dateClosed),       cell: r => <span className="em-cm">{fmtDate(r.dateClosed)}</span> },
  { key: 'destruction', label: 'Destruction',        w: 110, filter: 'date',   text: r => fmtDate(r.destructionDate),  cell: r => <span className="em-cm">{fmtDate(r.destructionDate)}</span> },
  { key: 'became',      label: 'Became client',      w: 130, filter: 'date',   text: r => fmtDate(r.dateBecameClient), cell: r => <span className="em-cm">{fmtDate(r.dateBecameClient)}</span> },
  { key: 'originator',  label: 'Originator',         w: 100, filter: 'select', facet: 'originators', text: r => r.originatorFE1 || '', cell: r => r.originatorFE1 || '-' },
];
const COL_BY_KEY = Object.fromEntries(COLUMNS.map(c => [c.key, c]));
const DEFAULT_ORDER = COLUMNS.map(c => c.key);
const DEFAULT_WIDTHS = Object.fromEntries(COLUMNS.map(c => [c.key, c.w]));
const COLS_STORE = 'em.cols.v2';

// Field maps for the client-side filter/sort.
const SELECT_FIELD = { status: 'mattStatus', partner: 'matterPartner', fe: 'matterFE', originator: 'originatorFE1' };
const DATE_FIELD   = { opened: 'dateOpened', closed: 'dateClosed', destruction: 'destructionDate', became: 'dateBecameClient' };
const SORT_FIELD   = { client: 'clientNumber', name: 'clientName', description: 'matterDescription', status: 'mattStatus', partner: 'matterPartner', fe: 'matterFE', opened: 'dateOpened', closed: 'dateClosed', destruction: 'destructionDate', became: 'dateBecameClient', originator: 'originatorFE1' };
const DATE_FIELDS_SET = new Set(['dateOpened', 'dateClosed', 'destructionDate', 'dateBecameClient']);
const tieBreak = (a, b) => (a.clientNumber - b.clientNumber) || ((a.matterRef || 0) - (b.matterRef || 0));

const csvCell = (v) => { const s = String(v ?? ''); return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };

// ─── IndexedDB cache (the dataset is a static snapshot) ────────────────────
// Whole dataset cached under one key + a version. Bump CACHE_VERSION whenever
// the data is re-imported so every browser re-fetches once; the Refresh button
// forces it too.
const IDB_NAME = 'evomatters', IDB_STORE = 'kv', CACHE_KEY = 'all', CACHE_VERSION = 1;
function idbOpen() {
  return new Promise((resolve, reject) => {
    try {
      const r = indexedDB.open(IDB_NAME, 1);
      r.onupgradeneeded = () => r.result.createObjectStore(IDB_STORE);
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    } catch (e) { reject(e); }
  });
}
async function idbGet(key) {
  try {
    const db = await idbOpen();
    return await new Promise((resolve, reject) => {
      const rq = db.transaction(IDB_STORE, 'readonly').objectStore(IDB_STORE).get(key);
      rq.onsuccess = () => resolve(rq.result);
      rq.onerror = () => reject(rq.error);
    });
  } catch (_) { return undefined; }
}
async function idbSet(key, val) {
  try {
    const db = await idbOpen();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).put(val, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (_) { /* ignore - falls back to re-fetch next time */ }
}

// Fields shown in the click-through detail popup (every column, incl. ones not
// in the table). Status is rendered as a pill; the rest as text.
const DETAIL_FIELDS = [
  ['Matter description', r => r.matterDescription || '-'],
  ['Status',             r => r.mattStatus || '-'],
  ['Matter partner',     r => r.matterPartner || '-'],
  ['Fee earner',         r => r.matterFE || '-'],
  ['Date opened',        r => fmtDate(r.dateOpened) || '-'],
  ['Date closed',        r => fmtDate(r.dateClosed) || '-'],
  ['Became client',      r => fmtDate(r.dateBecameClient) || '-'],
  ['Destruction date',   r => fmtDate(r.destructionDate) || '-'],
  ['Originator',         r => r.originatorFE1 || '-'],
  ['Originator 2',       r => r.originatorFE2 || '-'],
  ['Originator 3',       r => r.originatorFE3 || '-'],
];

const S = {
  wrap:    { padding: '20px 24px', width: '100%', boxSizing: 'border-box' },
  sub:     { fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 0' },
  bar:     { display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', margin: '18px 0 14px' },
  search:  { flex: '1 1 260px', minWidth: 0, display: 'flex', alignItems: 'center', gap: 8,
             border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', background: 'var(--bg-card)' },
  input:   { border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: 'var(--text)', width: '100%', fontFamily: 'inherit' },
  count:   { fontSize: 12.5, color: 'var(--text-subtle)', marginLeft: 'auto', whiteSpace: 'nowrap' },
  empty:   { padding: '40px 10px', textAlign: 'center', color: 'var(--text-subtle)', fontSize: 14 },
  errBox:  { background: 'var(--st-closed-bg)', color: 'var(--st-closed)', padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: 13, margin: '12px 0' },
  pager:   { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, padding: '16px 0', fontSize: 13, color: 'var(--text-muted)' },
  btn:     { border: '1px solid var(--line)', background: 'var(--bg-card)', color: 'var(--text)', borderRadius: 'var(--radius-sm)',
             padding: '7px 12px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  btnOff:  { opacity: 0.45, cursor: 'not-allowed' },
  iconBtn: { border: '1px solid var(--line)', background: 'var(--bg-card)', color: 'var(--text-muted)', borderRadius: 'var(--radius-sm)',
             padding: '7px 9px', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  seg:     { display: 'inline-flex', border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' },
  segBtn:  { border: 'none', background: 'var(--bg-card)', color: 'var(--text-muted)', fontSize: 12.5, padding: '7px 12px', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  segOn:   { background: 'var(--bg-active)', color: 'var(--text)', fontWeight: 600 },
  card:    { border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)', padding: '12px 14px', marginBottom: 10, background: 'var(--bg-card)' },
  cardTop: { display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline' },
  cm:      { fontVariantNumeric: 'tabular-nums', fontSize: 12.5, color: 'var(--text-muted)', whiteSpace: 'nowrap' },
  menu:    { position: 'absolute', right: 0, top: '110%', zIndex: 21, background: 'var(--bg-card)', border: '1px solid var(--line)',
             borderRadius: 8, padding: 8, minWidth: 190, boxShadow: 'var(--shadow-pop)' },
  menuRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', fontSize: 13, cursor: 'pointer', color: 'var(--text)' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal:   { background: 'var(--bg-card)', border: '1px solid var(--line)', borderRadius: 12, width: '100%', maxWidth: 480, maxHeight: '85vh', overflow: 'auto', boxShadow: 'var(--shadow-pop)' },
  mHead:   { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, padding: '16px 18px', borderBottom: '1px solid var(--line)', position: 'sticky', top: 0, background: 'var(--bg-card)' },
  dRow:    { display: 'flex', gap: 12, padding: '9px 18px', fontSize: 13, borderBottom: '1px solid var(--line)' },
  dLabel:  { width: 140, flexShrink: 0, color: 'var(--text-subtle)' },
  dValue:  { color: 'var(--text)', minWidth: 0, wordBreak: 'break-word' },
  xbtn:    { border: 'none', background: 'transparent', fontSize: 22, lineHeight: 1, color: 'var(--text-subtle)', cursor: 'pointer', padding: 0 },
};

const EM_CSS = `
.em-tablewrap { border: 1px solid var(--line); border-radius: 10px; overflow: auto; max-height: calc(100vh - 250px); background: var(--bg-card); }
.em-table { border-collapse: collapse; font-size: 13px; table-layout: fixed; }
.em-table thead th { position: sticky; top: 0; z-index: 1; background: var(--bg-sub); border-bottom: 1px solid var(--line-strong); white-space: nowrap; user-select: none; padding: 0; }
.em-th-inner { display: flex; align-items: center; gap: 4px; padding: 9px 10px 9px 12px; }
.em-th-label { flex: 1; min-width: 0; font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: var(--text-subtle); cursor: pointer; overflow: hidden; text-overflow: ellipsis; }
.em-th-label:hover { color: var(--text); }
.em-funnel { flex: none; border: none; background: transparent; padding: 2px; cursor: pointer; color: var(--text-subtle); display: flex; border-radius: 4px; }
.em-funnel:hover { background: var(--bg-hover); color: var(--text); }
.em-funnel.on { color: var(--accent); }
.em-resize { position: absolute; top: 0; right: 0; width: 7px; height: 100%; cursor: col-resize; }
.em-resize:hover { background: var(--accent); opacity: 0.5; }
.em-table tbody td { padding: 9px 12px; border-bottom: 1px solid var(--line); color: var(--text); vertical-align: top; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.em-table tbody tr:last-child td { border-bottom: none; }
.em-table tbody tr:hover { background: var(--bg-hover); }
.em-cm { font-variant-numeric: tabular-nums; white-space: nowrap; color: var(--text-muted); }
.em-pop { position: fixed; z-index: 30; width: 244px; background: var(--bg-card); border: 1px solid var(--line); border-radius: 8px; box-shadow: var(--shadow-pop); padding: 8px; }
.em-pop input { width: 100%; box-sizing: border-box; font-size: 12px; padding: 5px 7px; border: 1px solid var(--line); border-radius: 5px; background: var(--bg-card); color: var(--text); font-family: inherit; }
.em-pop .opts { max-height: 220px; overflow: auto; margin-top: 6px; }
.em-pop .opt { display: flex; align-items: center; gap: 8px; padding: 3px 4px; font-size: 12.5px; color: var(--text); cursor: pointer; }
.em-pop .opt input { width: auto; }
.em-pop .barrow { display: flex; justify-content: space-between; align-items: center; margin-top: 8px; }
.em-pop .lnk { background: none; border: none; padding: 0; color: var(--accent); cursor: pointer; font-size: 11.5px; font-family: inherit; }
.em-pop .lbl { font-size: 11px; color: var(--text-subtle); margin: 6px 0 2px; }
`;

export function EvolutionMattersView() {
  const isPhone = useIsPhone();
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState({}); // per column: select=array, date={from,to}
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('client');
  const [dir, setDir]   = useState('asc');
  const [allRows, setAllRows] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [colMenu, setColMenu] = useState(false);
  const [openFilter, setOpenFilter] = useState(null); // { key, x, y }
  const [popSearch, setPopSearch] = useState('');
  const [selected, setSelected] = useState(null); // row shown in the detail popup

  // Column order / widths / hidden, persisted so a user's layout sticks.
  const readStore = () => { try { return JSON.parse(localStorage.getItem(COLS_STORE) || 'null'); } catch (_) { return null; } };
  const [order, setOrder] = useState(() => {
    const s = readStore();
    if (s && Array.isArray(s.order)) {
      const kept = s.order.filter(k => COL_BY_KEY[k]);
      return [...kept, ...DEFAULT_ORDER.filter(k => !kept.includes(k))];
    }
    return DEFAULT_ORDER;
  });
  const [widths, setWidths] = useState(() => { const s = readStore(); return s && s.widths ? { ...DEFAULT_WIDTHS, ...s.widths } : DEFAULT_WIDTHS; });
  const [hidden, setHidden] = useState(() => { const s = readStore(); return s && Array.isArray(s.hidden) ? s.hidden.filter(k => COL_BY_KEY[k]) : []; });
  useEffect(() => {
    try { localStorage.setItem(COLS_STORE, JSON.stringify({ order, widths, hidden })); } catch (_) { /* private mode */ }
  }, [order, widths, hidden]);

  const dragKey = useRef(null);
  const onDragStart = (e, key) => { dragKey.current = key; e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', key); } catch (_) { /* IE */ } };
  const onDropCol = (e, key) => {
    e.preventDefault();
    const from = dragKey.current; dragKey.current = null;
    if (!from || from === key) return;
    setOrder(prev => {
      const arr = prev.filter(k => k !== from);
      const idx = arr.indexOf(key);
      arr.splice(idx < 0 ? arr.length : idx, 0, from);
      return arr;
    });
  };
  const onResizeStart = (e, key) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const startW = widths[key] ?? COL_BY_KEY[key].w;
    const move = (ev) => setWidths(prev => ({ ...prev, [key]: Math.max(60, startW + (ev.clientX - startX)) }));
    const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); document.body.style.cursor = ''; };
    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };
  const toggleHidden = (key) => setHidden(prev => {
    if (prev.includes(key)) return prev.filter(k => k !== key);
    if (DEFAULT_ORDER.length - prev.length <= 1) return prev; // keep at least one column
    return [...prev, key];
  });
  const resetLayout = () => { setOrder(DEFAULT_ORDER); setWidths(DEFAULT_WIDTHS); setHidden([]); };
  const resetView = () => {
    setQ(''); setFilters({}); setSort('client'); setDir('asc'); setPage(1);
    setOrder(DEFAULT_ORDER); setWidths(DEFAULT_WIDTHS); setHidden([]);
    setOpenFilter(null); setColMenu(false);
  };

  // Filter helpers.
  const setFilter = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));
  const toggleVal = (key, val) => setFilters(prev => {
    const cur = Array.isArray(prev[key]) ? prev[key] : [];
    return { ...prev, [key]: cur.includes(val) ? cur.filter(v => v !== val) : [...cur, val] };
  });
  const setDate = (key, part, val) => setFilters(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [part]: val } }));
  const hasFilter = (key) => {
    const f = filters[key]; const col = COL_BY_KEY[key];
    if (!f || !col) return false;
    if (col.filter === 'select') return Array.isArray(f) && f.length > 0;
    if (col.filter === 'date') return !!(f.from || f.to);
    return false;
  };
  const openPop = (e, key) => {
    e.stopPropagation();
    const th = e.currentTarget.closest('th');
    const r = (th || e.currentTarget).getBoundingClientRect();
    setPopSearch('');
    setOpenFilter(prev => (prev && prev.key === key) ? null : { key, x: r.left, y: r.bottom });
  };

  // Load the whole dataset once (cache-first), then everything is client-side.
  const loadAll = async (force) => {
    setLoading(true); setError(null);
    try {
      if (!force) {
        const cached = await idbGet(CACHE_KEY);
        if (cached && cached.version === CACHE_VERSION && Array.isArray(cached.rows)) {
          setAllRows(cached.rows); setLoading(false); return;
        }
      }
      const res = await emApi('GET', '/evolution-matters?all=1');
      const rows = res?.rows || [];
      setAllRows(rows);
      idbSet(CACHE_KEY, { version: CACHE_VERSION, rows });
    } catch (e) {
      setError(e.message || 'Could not load matters.');
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(false); }, []);

  // Close the detail popup on Escape.
  useEffect(() => {
    if (!selected) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  // Facets derived from the loaded data (no separate request).
  const facets = useMemo(() => {
    const rows = allRows || [];
    const uniq = (field) => Array.from(new Set(rows.map(r => r[field]).filter(v => v !== null && v !== undefined && v !== '')))
      .map(String).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    return { statuses: uniq('mattStatus'), partners: uniq('matterPartner'), fes: uniq('matterFE'), originators: uniq('originatorFE1') };
  }, [allRows]);

  // Client-side search + filter + sort.
  const filtered = useMemo(() => {
    const rows = allRows || [];
    const qq = q.trim().toLowerCase();
    const numMatch = q.trim().match(/^0*(\d+)(?:[.\-/]0*(\d+))?$/);
    let out = rows.filter(r => {
      if (qq) {
        let ok = (r.clientName || '').toLowerCase().includes(qq) || (r.matterDescription || '').toLowerCase().includes(qq);
        if (!ok && numMatch) {
          const c = parseInt(numMatch[1], 10);
          ok = numMatch[2] ? (r.clientNumber === c && r.matterRef === parseInt(numMatch[2], 10)) : (r.clientNumber === c);
        }
        if (!ok) return false;
      }
      for (const key of Object.keys(filters)) {
        const col = COL_BY_KEY[key]; const f = filters[key];
        if (!col || !f) continue;
        if (col.filter === 'select' && Array.isArray(f) && f.length) {
          if (!f.includes(String(r[SELECT_FIELD[key]] ?? ''))) return false;
        } else if (col.filter === 'date' && (f.from || f.to)) {
          const raw = r[DATE_FIELD[key]];
          const d = raw ? String(raw).slice(0, 10) : '';
          if (!d) return false;
          if (f.from && d < f.from) return false;
          if (f.to && d > f.to) return false;
        }
      }
      return true;
    });
    const field = SORT_FIELD[sort] || 'clientNumber';
    const isDate = DATE_FIELDS_SET.has(field);
    const d1 = dir === 'desc' ? -1 : 1;
    out = out.slice().sort((a, b) => {
      const av = a[field], bv = b[field];
      const an = (av === null || av === undefined || av === '');
      const bn = (bv === null || bv === undefined || bv === '');
      if (an && bn) return tieBreak(a, b);
      if (an) return 1;   // blanks always last
      if (bn) return -1;
      let c;
      if (isDate) c = new Date(av) - new Date(bv);
      else if (typeof av === 'number' && typeof bv === 'number') c = av - bv;
      else c = String(av).localeCompare(String(bv), undefined, { numeric: true });
      if (c === 0) return tieBreak(a, b);
      return c * d1;
    });
    return out;
  }, [allRows, q, filters, sort, dir]);

  useEffect(() => { setPage(1); }, [q, filters, sort, dir]);

  const onSort = (key) => {
    if (sort === key) setDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(key); setDir('asc'); }
  };
  const arrow = (key) => (sort === key ? (dir === 'asc' ? ' ▲' : ' ▼') : '');

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const cols = order.map(k => COL_BY_KEY[k]).filter(c => c && !hidden.includes(c.key));
  const tableWidth = cols.reduce((sum, c) => sum + (widths[c.key] ?? c.w), 0);

  // Instant CSV of the full filtered set, built from memory.
  const exportCsv = () => {
    const header = cols.map(c => csvCell(c.label)).join(',');
    const body = filtered.map(r => cols.map(c => csvCell(c.text ? c.text(r) : '')).join(',')).join('\r\n');
    const csv = '﻿' + [header, body].join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'evolution-matters.csv';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const renderPop = (col) => {
    if (col.filter === 'select') {
      const opts = (facets[col.facet] || []).filter(o => o.toLowerCase().includes(popSearch.toLowerCase()));
      const sel = Array.isArray(filters[col.key]) ? filters[col.key] : [];
      return (
        <>
          <input type="text" placeholder="Search values…" value={popSearch} onChange={e => setPopSearch(e.target.value)} autoFocus/>
          <div className="barrow">
            <button className="lnk" onClick={() => setFilters(p => ({ ...p, [col.key]: Array.from(new Set([...(p[col.key] || []), ...opts])) }))}>Select all</button>
            <button className="lnk" onClick={() => setFilter(col.key, [])}>Clear</button>
          </div>
          <div className="opts">
            {opts.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-subtle)', padding: '6px 4px' }}>No values</div>}
            {opts.map(o => (
              <label key={o} className="opt">
                <input type="checkbox" checked={sel.includes(o)} onChange={() => toggleVal(col.key, o)}/>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{o}</span>
              </label>
            ))}
          </div>
        </>
      );
    }
    if (col.filter === 'date') {
      const f = filters[col.key] || {};
      return (
        <>
          <div className="lbl" style={{ marginTop: 0 }}>From</div>
          <input type="date" value={f.from || ''} onChange={e => setDate(col.key, 'from', e.target.value)}/>
          <div className="lbl">To</div>
          <input type="date" value={f.to || ''} onChange={e => setDate(col.key, 'to', e.target.value)}/>
          <div className="barrow"><span/><button className="lnk" onClick={() => setFilter(col.key, {})}>Clear</button></div>
        </>
      );
    }
    return null;
  };

  return (
    <div style={S.wrap}>
      <style>{EM_CSS}</style>
      <p style={S.sub}>Search or filter the archived Evolution client/matter list. Read-only. Click a row for full details, a column's caret to filter; drag headers to reorder or resize.</p>

      <div style={S.bar}>
        <div style={S.search}>
          <Icon name="search" size={14}/>
          <input style={S.input} placeholder="Search client name, matter, or number" value={q} onChange={e => setQ(e.target.value)}/>
          {q && <button onClick={() => setQ('')} style={{ ...S.input, width: 'auto', cursor: 'pointer', color: 'var(--text-subtle)' }} title="Clear">×</button>}
        </div>

        {facets.statuses.length > 0 && (
          <div style={S.seg}>
            {['', ...facets.statuses].map((v, i) => {
              const sel = Array.isArray(filters.status) ? filters.status : [];
              const active = v === '' ? sel.length !== 1 : (sel.length === 1 && sel[0] === v);
              return (
                <button key={v || 'all'} onClick={() => setFilter('status', v === '' ? [] : [v])}
                  style={{ ...S.segBtn, ...(active ? S.segOn : {}), ...(i > 0 ? { borderLeft: '1px solid var(--line)' } : {}) }}>
                  {v || 'All'}
                </button>
              );
            })}
          </div>
        )}

        <span style={S.count}>{loading ? 'Loading…' : `${total.toLocaleString('en-GB')} result${total === 1 ? '' : 's'}`}</span>

        {!isPhone && <button style={S.iconBtn} onClick={() => loadAll(true)} disabled={loading} title="Reload the latest data from the server"><Icon name="refresh" size={14}/></button>}
        {!isPhone && <button style={S.btn} onClick={resetView} title="Clear search, filters, sort and column layout">Reset view</button>}
        {!isPhone && <button style={{ ...S.btn, ...((total === 0) ? S.btnOff : {}) }} onClick={exportCsv} disabled={total === 0} title="Download matching rows as CSV">Export CSV</button>}

        {!isPhone && (
          <div style={{ position: 'relative' }}>
            <button style={S.btn} onClick={() => setColMenu(o => !o)}>Columns</button>
            {colMenu && (
              <>
                <div onClick={() => setColMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 20 }}/>
                <div style={S.menu}>
                  {COLUMNS.map(c => (
                    <label key={c.key} style={S.menuRow}>
                      <input type="checkbox" checked={!hidden.includes(c.key)} onChange={() => toggleHidden(c.key)}/>
                      {c.label}
                    </label>
                  ))}
                  <div style={{ borderTop: '1px solid var(--line)', marginTop: 6, paddingTop: 6 }}>
                    <button style={{ ...S.btn, width: '100%' }} onClick={() => { resetLayout(); setColMenu(false); }}>Reset columns</button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {loading && <div style={S.empty}>Loading matters…</div>}
      {!loading && error && <div style={S.errBox}>{error}</div>}
      {!loading && !error && total === 0 && <div style={S.empty}>No matters match your search.</div>}

      {!loading && !error && total > 0 && (isPhone ? (
        <div>
          {rows.map((r, i) => (
            <div key={i} style={{ ...S.card, cursor: 'pointer' }} onClick={() => setSelected(r)}>
              <div style={S.cardTop}>
                <strong style={{ fontSize: 14, color: 'var(--text)' }}>{r.clientName || '-'}</strong>
                <StatusTag status={r.mattStatus}/>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', margin: '3px 0' }}>{r.matterDescription || '-'}</div>
              <div style={S.cm}>{fmtCM(r.clientNumber, r.matterRef)}</div>
              <div style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 6, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <span>Partner: {r.matterPartner || '-'}</span>
                <span>FE: {r.matterFE || '-'}</span>
                {r.dateClosed && <span>Closed: {fmtDate(r.dateClosed)}</span>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="em-tablewrap">
          <table className="em-table" style={{ width: tableWidth, minWidth: '100%' }}>
            <colgroup>
              {cols.map(c => <col key={c.key} style={{ width: (widths[c.key] ?? c.w) + 'px' }}/>)}
            </colgroup>
            <thead>
              <tr>
                {cols.map(c => (
                  <th key={c.key} onDragOver={e => e.preventDefault()} onDrop={e => onDropCol(e, c.key)}>
                    <div className="em-th-inner">
                      <span className="em-th-label" draggable onDragStart={e => onDragStart(e, c.key)} onClick={() => onSort(c.key)} title="Drag to move, click to sort">
                        {c.label}{arrow(c.key)}
                      </span>
                      {c.filter && (
                        <button className={`em-funnel${hasFilter(c.key) ? ' on' : ''}`} onClick={e => openPop(e, c.key)} title="Filter">
                          <Icon name="chevron" size={12}/>
                        </button>
                      )}
                    </div>
                    <span className="em-resize" onMouseDown={e => onResizeStart(e, c.key)} onClick={e => e.stopPropagation()}/>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} onClick={() => setSelected(r)} style={{ cursor: 'pointer' }}>
                  {cols.map(c => <td key={c.key} title={c.title ? c.title(r) : (c.text ? c.text(r) : undefined)}>{c.cell(r)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {!loading && !error && total > PAGE_SIZE && (
        <div style={S.pager}>
          <button style={{ ...S.btn, ...(page <= 1 ? S.btnOff : {}) }} onClick={() => page > 1 && setPage(p => p - 1)} disabled={page <= 1}>Previous</button>
          <span>Page {page} of {totalPages}</span>
          <button style={{ ...S.btn, ...(page >= totalPages ? S.btnOff : {}) }} onClick={() => page < totalPages && setPage(p => p + 1)} disabled={page >= totalPages}>Next</button>
        </div>
      )}

      {openFilter && COL_BY_KEY[openFilter.key] && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 29 }} onClick={() => setOpenFilter(null)}/>
          <div className="em-pop" style={{ left: Math.max(8, Math.min(openFilter.x, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 252)), top: openFilter.y + 4 }}>
            {renderPop(COL_BY_KEY[openFilter.key])}
          </div>
        </>
      )}

      {selected && (
        <div style={S.overlay} onClick={() => setSelected(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.mHead}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', wordBreak: 'break-word' }}>{selected.clientName || '-'}</div>
                <div style={{ ...S.cm, marginTop: 2 }}>{fmtCM(selected.clientNumber, selected.matterRef)}</div>
              </div>
              <button style={S.xbtn} onClick={() => setSelected(null)} title="Close" aria-label="Close">×</button>
            </div>
            <div>
              {DETAIL_FIELDS.map(([label, get]) => (
                <div key={label} style={S.dRow}>
                  <span style={S.dLabel}>{label}</span>
                  <span style={S.dValue}>{label === 'Status' ? <StatusTag status={selected.mattStatus}/> : get(selected)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Chromeless standalone page (no app sidebar) for the /evomatters URL - shareable
// with people outside IT (e.g. the CFO), gated by the backend allowlist.
export function EvolutionMattersStandalone() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg, #F6F5F3)', color: 'var(--text, #1F2937)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 24px', borderBottom: '1px solid var(--line, #E7E5E2)' }}>
        <span style={{ fontSize: 16, fontWeight: 700 }}>Evolution Matters</span>
        <span style={{ fontSize: 12, color: 'var(--text-subtle, #9CA3AF)' }}>Lee &amp; Thompson</span>
      </div>
      <EvolutionMattersView/>
    </div>
  );
}
